import { TypographyGroupValue } from '@makyoapp/makyo-common'
import { Alert, HoverEffectVariant, HoverIntensity, MaterialIcon, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'

interface CompAlertWrapperCoreProps {
  infos: CoreComponentInfos
}

export function CompAlertWrapperCore({
  infos: { settings, currentParentBg, containerClient, detailTableId, detailRowIndex, component },
}: CompAlertWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const title = useParseVariableText(
    settings[ComponentSettingKey.AlertDataTitle],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const description = useParseVariableText(
    settings[ComponentSettingKey.AlertDataDescription],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const icon = getSafely(settings, [ComponentSettingKey.AlertDataIcon], '')

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.AlertDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.AlertDesignAlignment] ?? 'parent'
  const margin = settings[ComponentSettingKey.AlertDesignMargin] as RawSpacingInput
  const padding = settings[ComponentSettingKey.AlertDesignPadding] as RawSpacingInput

  // color
  const colorVariant = settings[ComponentSettingKey.AlertDesignColorVariant] ?? 'primary-40'

  // typography
  const typography = settings[ComponentSettingKey.AlertDesignTypography] as TypographyGroupValue
  const titleTypography = typography[ComponentSettingKey.AlertDesignTitleTypography]
  const descriptionTypography = typography[ComponentSettingKey.AlertDesignDescriptionTypography]

  const hoverEffect = settings[ComponentSettingKey.AlertDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  const outline = settings[ComponentSettingKey.AlertDesignOutline]
  const elevation = settings[ComponentSettingKey.AlertDesignElevation]

  const renderAlert = () => {
    return (
      <Alert
        title={title}
        description={description}
        icon={icon ? <MaterialIcon icon={icon} size={20} /> : null}
        width={width}
        alignment={alignment}
        margin={margin}
        padding={padding}
        colorVariant={colorVariant}
        titleTypography={titleTypography}
        descriptionTypography={descriptionTypography}
        outline={outline}
        elevation={elevation}
        hoverEffect={hoverEffect as HoverEffectVariant}
        hoverIntensity={hoverIntensity as HoverIntensity}
      />
    )
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {renderAlert()}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return renderAlert()
}
